/**
 * Data Dictionary component displays list of RTK keys and can perform download, filter, edit add and delete of the RTK Keys
 * This Component is rendered as part of Admin -> system Settings -> Data Dictionary Tab
 * It is available in the route - /admin/systemsettings
 */
import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';

import { AppContext } from '../../../../../RoutesWithAuth';
import { UserPreferencesContext } from '../../../../common/UserPreferences';

import axios from '../../../../common/AxiosConfig';

import CustomGrid from '../../customgrid';
import EditDialog from '../../customgrid/EditDialog';

import { makeStyles } from "@mui/styles";

import moment from 'moment';

import Papa from 'papaparse';

import { IconButton, Tooltip, Button } from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import GetAppIcon from '@mui/icons-material/GetApp';

import { LoadingIndicator } from '../../reports/reconcilationreport/MuiStyled';

import DeleteDialog from './DeleteDialog';
import { ADMIN_PERMISSIONS } from '../../../../common/RolePermissions';
import PermissionButton from '../../PermissionButton';

const getEditFields = (apps) => [
    { name: 'display_name', label: 'Source', type: 'select', options: apps?.map(({ display }) => display) || [], required: true },
    { name: 'element_type', label: 'Element Type', type: 'select', options: ['Attribute', 'Transaction'], required: true },
    { name: 'key', label: 'Key', type: 'text', required: true },
    { name: 'display_term', label: 'Display Term', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'text', required: true },
    { name: 'display_term_fr', label: 'Display Term French', type: 'text' },
    { name: 'description_fr', label: 'Description French', type: 'text' },
    { name: 'data_type', label: 'Data Type', type: 'select', options: ['Text', 'File-to-Attach-to-Privacy-Report'] },
    { name: 'review_category', label: 'Review Category', type: 'select', options: ['First Name', 'Last Name', 'Not Applicable', 'VIN'] },
    { name: 'tips', label: 'TIPS', type: 'select', options: ['1.Development', '2.Legal Review', '3.Approved', '4.Declined'] },
    { name: 'qb64', label: 'QB64', type: 'select', options: ['1.Development', '2.Legal Review', '3.Approved', '4.Declined'] },
    { name: 'overwrite', label: 'Revise Attribute Value', type: 'select', options: ['Yes', 'No'] },
    { name: 'canned_message', label: 'Updated Attribute Value', type: 'text' },
    { name: 'notes', label: 'Notes', type: 'text' },
    { name: 'applicable_requestor', label: 'Applicable Requestor', type: 'multiselect', isMulti: true, options: ['B2B', 'Customer', 'Employee', 'TCI Customer'],
        requiredIf: (formValues) => formValues?.is_active === 'True',
        validator: (value, formValues) => {
            if (formValues?.is_active === 'True' && (!value || (Array.isArray(value) && value.length === 0))) {
                return '"Applicable Requestor" is mandatory for active keys';
            }
            return null;
        }
    },
    { name: 'is_active', label: 'Is Active', type: 'select', options: ['True', 'False'] },
];

const useStyles = makeStyles(() => ({
    wrapper: {
        height: 'calc(100% - 50px)',
        '& .isInactive-row td': {
            color: '#e91e63 !important'
        },
        '& .transaction-row td': {
            fontWeight: 'bold'
        }
    },
    toolbar: {
        display: 'flex',
        gap: '8px',
        marginBottom: '8px',
        alignItems: 'center',
    }
}));

function DataDictionary(props) {
    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const apps = context.apps.get;
    const isAdmin = context.isAdmin.get;
    const { getPreferences, savePreferences, loaded: prefsLoaded } = useContext(UserPreferencesContext);

    const [appsData, setAppsData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSavingPreferences, setIsSavingPreferences] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [isNewRecord, setIsNewRecord] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteRecord, setDeleteRecord] = useState(null);
    const [activeFilters, setActiveFilters] = useState({});
    const [activeSortModel, setActiveSortModel] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(100);
    const [defaultHiddenColumns, setDefaultHiddenColumns] = useState({});

    const classes = useStyles();

    // Load saved preferences on mount
    useEffect(() => {
        if (!prefsLoaded) return;
        const prefs = getPreferences('DATA_DICTIONARY');
        if (prefs.filters && Object.keys(prefs.filters).length > 0) setActiveFilters(prefs.filters);
        if (prefs.sort && prefs.sort.field) setActiveSortModel([prefs.sort]);
        if (prefs.hiddenColumns && Object.keys(prefs.hiddenColumns).length > 0) setDefaultHiddenColumns(prefs.hiddenColumns);
        if (prefs.pageCount) setPageCount(prefs.pageCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefsLoaded]);

    const handleSaveDefaults = useCallback((gridState) => {
        setIsSavingPreferences(true);
        const startTime = Date.now();

        return savePreferences('DATA_DICTIONARY', {
            filters: gridState.filters,
            sort: gridState.sortModel?.[0] || {},
            hiddenColumns: gridState.hiddenColumns,
            pageCount: gridState.pageCount
        }).then(() => {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 2000 - elapsedTime);
            setTimeout(() => {
                setIsSavingPreferences(false);
                props.toggleError('Filter preferences saved successfully', 'success');
            }, remainingTime);
        }).catch(() => {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 2000 - elapsedTime);
            setTimeout(() => {
                setIsSavingPreferences(false);
                props.toggleError('Failed to save filter preferences', 'error');
            }, remainingTime);
        });
    }, [savePreferences, props]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, activeFilters]);

    const getFilterPayload = useCallback((isDownload = false) => {
        let payload = {};
        Object.keys(activeFilters).forEach(key => {
            const filter = activeFilters[key];
            if (!filter || !filter.value) return;
            let paramValue = filter.value;
            let paramName = key;

            // Handle array values from multi-select
            if (Array.isArray(paramValue)) {
                if (paramValue.includes('All') || paramValue.length === 0) return;
                paramValue = paramValue.join(',');
            }

            if (key === 'is_active') {
                paramValue = String(paramValue).includes('True') ? 'true' : 'false';
                paramName = 'filter';
            } else if (key === 'overwrite') {
                paramValue = String(paramValue).includes('Yes');
            } else if (key === 'display_name') {
                paramName = 'source';
                // Map display names to app names
                const displayNames = String(paramValue).split(',');
                paramValue = displayNames.map(d => apps?.find(({ display }) => display === d.trim())?.name || d.trim()).join(',');
            }
            payload[paramName] = paramValue;
        });
        if (isDownload) payload.download = true;
        return payload;
    }, [activeFilters, apps]);

    const fetchData = useCallback((isDownload = false) => {
        setIsLoading(true);
        axios.get('/rtk_keys', {
            headers: { Authorization: authToken },
            params: getFilterPayload(isDownload),
        }).then(res => {
            setIsLoading(false);
            if (isDownload) {
                handleDownloadResponse(res);
            } else {
                setAppsData(Array.isArray(res.data) ? res.data : []);
            }
        }).catch(err => {
            setIsLoading(false);
            console.log(err);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, getFilterPayload]);

    const handleDownloadResponse = (res) => {
        if (res.status === 204) {
            props.toggleError("No records to download");
            return;
        }
        const csv = Papa.unparse(res.data);
        const downloadLink = document.createElement("a");
        const blob = new Blob(["\ufeff", csv]);
        const url = URL.createObjectURL(blob);
        downloadLink.href = url;
        const timestamp = moment().format("YYYYMMDD");
        downloadLink.download = `CPS_DataDictionary_${timestamp}.csv`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    };

    const handleEditClick = useCallback((row) => {
        setEditingRow(row);
        setIsNewRecord(false);
        setEditDialogOpen(true);
    }, []);

    const handleAddClick = useCallback(() => {
        setEditingRow({
            display_name: '', element_type: '', key: '', display_term: '', description: '',
            display_term_fr: '', description_fr: '', data_type: 'Text',
            review_category: 'Not Applicable', tips: '1.Development', qb64: '',
            overwrite: 'No', canned_message: '', notes: '', applicable_requestor: [],
            is_active: 'False'
        });
        setIsNewRecord(true);
        setEditDialogOpen(true);
    }, []);

    const handleEditSave = useCallback((formValues) => {
        const appNameApi = apps?.find(({ display }) => display === formValues.display_name)?.name || formValues.display_name;
        const payload = {
            app_name: appNameApi,
            element_type: formValues.element_type,
            is_active: formValues.is_active === 'True',
            display_term: formValues.display_term,
            desc: formValues.description,
            tips: formValues.tips,
            qb64: formValues.qb64 || '',
            review_category: formValues.review_category,
            sample: '',
            data_type: formValues.data_type,
            overwrite: formValues.overwrite === 'Yes',
            canned_message: formValues.canned_message || '',
            "Description-FR": formValues.description_fr,
            "Display_Term-FR": formValues.display_term_fr,
            notes: formValues.notes,
            applicable_requestor: formValues.applicable_requestor
        };

        const request = isNewRecord
            ? axios.post(`/rtk_keys/${formValues.key.trim()}`, payload, { headers: { Authorization: authToken } })
            : axios.put(`/rtk_keys/${editingRow.originalKey.trim()}`, { ...payload, new_key: formValues.key }, { headers: { Authorization: authToken } });

        request.then(() => {
            fetchData();
            if (isNewRecord) {
                props.toggleError('Key added successfully', 'success');
            } else {
                props.toggleError('Key updated successfully', 'success');
            }
        }).catch(err => {
            // Extract error message with multiple fallback strategies
            let errorMsg = 'An error occurred. Please try again.';
            
            // Check for message in response.data
            if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
            } 
            // Check for error property in response.data
            else if (err.response?.data?.error) {
                errorMsg = err.response.data.error;
            }
            // Check for errors array (common validation response format)
            else if (err.response?.data?.errors && Array.isArray(err.response.data.errors) && err.response.data.errors.length > 0) {
                errorMsg = err.response.data.errors[0].message || err.response.data.errors[0];
            }
            // Check for message string directly in response.data
            else if (typeof err.response?.data === 'string') {
                errorMsg = err.response.data;
            }
            // Network/timeout error handling
            else if (!err.response && err.message) {
                errorMsg = 'Network error: ' + err.message;
            }
            
            // Detect duplicate key scenarios and provide specific message
            if (isNewRecord && (err.response?.status === 409 || errorMsg.toLowerCase().includes('already exists') || errorMsg.toLowerCase().includes('key already exists'))) {
                props.toggleError('This key already exists. Please use a different key.', 'error');
            } else {
                props.toggleError(errorMsg, 'error');
            }
        }).finally(() => {
            // Always close the dialog, whether success or error
            setEditDialogOpen(false);
            setEditingRow(null);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, apps, isNewRecord, editingRow]);

    const handleEditClose = useCallback(() => {
        setEditDialogOpen(false);
        setEditingRow(null);
    }, []);

    const handleDeleteClick = useCallback((row) => {
        setDeleteRecord(row);
        setOpenDeleteDialog(true);
    }, []);

    const handleDeleteDialogClose = useCallback(() => {
        setDeleteRecord(null);
        setOpenDeleteDialog(false);
    }, []);

    const handleDeleteDialogSubmit = useCallback(() => {
        if (!deleteRecord) return;
        axios.delete(`/rtk_keys/${deleteRecord.key}`, {
            headers: { Authorization: authToken }
        }).then(() => {
            fetchData();
            props.toggleError('Key deleted successfully', 'success');
        }).catch(err => {
            // Extract error message with multiple fallback strategies
            let errorMsg = 'Failed to delete key. Please try again.';
            
            // Check for message in response.data
            if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
            }
            // Check for error property in response.data
            else if (err.response?.data?.error) {
                errorMsg = err.response.data.error;
            }
            // Check for errors array (common validation response format)
            else if (err.response?.data?.errors && Array.isArray(err.response.data.errors) && err.response.data.errors.length > 0) {
                errorMsg = err.response.data.errors[0].message || err.response.data.errors[0];
            }
            // Check for message string directly in response.data
            else if (typeof err.response?.data === 'string') {
                errorMsg = err.response.data;
            }
            // Network/timeout error handling
            else if (!err.response && err.message) {
                errorMsg = 'Network error: ' + err.message;
            }
            
            props.toggleError(errorMsg, 'error');
        }).finally(() => {
            // Always close the dialog, whether success or error
            setDeleteRecord(null);
            setOpenDeleteDialog(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, deleteRecord]);

    const handleRemoteFiltering = useCallback((filters) => {
        setActiveFilters(filters);
        setCurrentPage(1);
    }, []);

    const handleRemoteSorting = useCallback((sortModel) => {
        setActiveSortModel(sortModel);
    }, []);

    const handleResetAllFilters = useCallback(() => {
        setActiveFilters({});
        setActiveSortModel([]);
        setCurrentPage(1);
    }, []);

    const gridData = useMemo(() => {
        let data = appsData?.map((row, i) => ({
            id: `${row.app_name}--${row.key}`,
            rownum: i + 1,
            ...row,
            originalKey: row.originalKey || row.key,
            display_name: row.display_name || apps?.find(a => a.name === row.app_name)?.display || row.app_name,
            is_active: row.is_active ? 'True' : 'False',
            overwrite: row.overwrite ? 'Yes' : 'No',
            applicable_requestor_string: row.applicable_requestor || '',
        })) || [];

        // Apply local sorting
        if (activeSortModel.length > 0) {
            const sort = activeSortModel[0];
            data.sort((a, b) => {
                const aVal = a[sort.field] || '';
                const bVal = b[sort.field] || '';
                const cmp = String(aVal).localeCompare(String(bVal));
                return sort.sort === 'asc' ? cmp : -cmp;
            });
        }

        // Re-number after sorting
        data.forEach((row, i) => { row.rownum = i + 1; });

        return data;
    }, [appsData, apps, activeSortModel]);

    // Paginate locally
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageCount;
        return gridData.slice(start, start + pageCount);
    }, [gridData, currentPage, pageCount]);

    const columns = useMemo(() => {
        const appNames = apps?.map(({ display }) => display) || [];
        const cols = [
            { name: "#", mapping: "rownum", width: 50, frozen: true, sortable: false, hideable: false, disableColumnMenu: true, align: 'center' },
            { name: "Source", mapping: "display_name", width: 150, frozen: true, sortable: true, type: 'singleSelect', valueOptions: appNames, displayTootip: ["display_name"] },
            { name: "Element Type", mapping: "element_type", width: 125, frozen: true, sortable: true, type: 'singleSelect', valueOptions: ['Attribute', 'Transaction'] },
            { name: "Key", mapping: "key", width: 230, frozen: true, hideable: false, sortable: true, displayTootip: ["key", 20] },
            { name: "Display Term", mapping: "display_term", width: 150, sortable: true, displayTootip: ["display_term", 10] },
            { name: "Description", mapping: "description", width: 150, sortable: true, displayTootip: ["description", 30] },
            { name: "Display Term French", mapping: "display_term_fr", width: 160, sortable: true, displayTootip: ["display_term_fr", 8] },
            { name: "Description French", mapping: "description_fr", width: 150, sortable: true, displayTootip: ["description_fr", 8] },
            { name: "Data Type", mapping: "data_type", width: 165, sortable: true, type: 'singleSelect', valueOptions: ['Text', 'File-to-Attach-to-Privacy-Report'], displayTootip: ["data_type", 10] },
            { name: "Review Category", mapping: "review_category", width: 165, sortable: true, type: 'singleSelect', valueOptions: ['First Name', 'Last Name', 'Not Applicable', 'VIN'] },
            { name: "TIPS", mapping: "tips", width: 100, sortable: true, type: 'singleSelect', valueOptions: ['1.Development', '2.Legal Review', '3.Approved', '4.Declined'] },
            { name: "QB64", mapping: "qb64", width: 100, sortable: true, type: 'singleSelect', valueOptions: ['1.Development', '2.Legal Review', '3.Approved', '4.Declined'] },
            { name: "Revise Attribute Value", mapping: "overwrite", width: 180, sortable: true, type: 'singleSelect', valueOptions: ['Yes', 'No'] },
            { name: "Updated Attribute Value", mapping: "canned_message", width: 180, sortable: true, displayTootip: ["canned_message", 10] },
            { name: "Notes", mapping: "notes", width: 150, sortable: true, displayTootip: ["notes", 10] },
            { name: "Applicable Requestor", mapping: "applicable_requestor_string", width: 200, sortable: true, type: 'singleSelect', valueOptions: ['B2B', 'Customer', 'Employee', 'TCI Customer'], displayTootip: ["applicable_requestor_string", 10] },
            { name: "Is Active", mapping: "is_active", width: 125, sortable: true, type: 'singleSelect', valueOptions: ['True', 'False'] },
        ];

        cols.push({
            name: "Edit", mapping: "actions_edit", width: 70, sortable: false, hideable: false, disableColumnMenu: true, align: 'center',
            renderCell: ({ row }) => (
                <Tooltip title="Edit">
                    <PermissionButton iconOnly permission={ADMIN_PERMISSIONS.SYSTEM_SETTINGS_DATA_EDIT} size="small" data-testid="edit" onClick={(e) => { e.stopPropagation(); handleEditClick(row); }}>
                        <Edit fontSize="small" />
                    </PermissionButton>
                </Tooltip>
            )
        });
        cols.push({
            name: "Delete", mapping: "actions_delete", width: 80, sortable: false, hideable: false, disableColumnMenu: true, align: 'center',
            renderCell: ({ row }) => (
                <Tooltip title="Delete">
                    <PermissionButton iconOnly permission={ADMIN_PERMISSIONS.SYSTEM_SETTINGS_DATA_DELETE} size="small" data-testid="delete" onClick={(e) => { e.stopPropagation(); handleDeleteClick(row); }}>
                        <Delete fontSize="small" />
                    </PermissionButton>
                </Tooltip>
            )
        });

        return cols;
    }, [apps, isAdmin, handleEditClick, handleDeleteClick]);

    const editFields = useMemo(() => getEditFields(apps), [apps]);

    const getRowClassName = useCallback((params) => {
        const classes = [];
        if (params?.row?.is_active === 'False') classes.push('isInactive-row');
        if (params?.row?.element_type === 'Transaction') classes.push('transaction-row');
        return classes.join(' ');
    }, []);

    return (
        <div className={classes.wrapper}>
            {(isLoading || isSavingPreferences) && (<LoadingIndicator />)}

            <div className={classes.toolbar}>
                <Tooltip title="Add Key">
                    <PermissionButton permission={ADMIN_PERMISSIONS.SYSTEM_SETTINGS_DATA_ADD} variant="outlined" size="small" startIcon={<Add />} onClick={handleAddClick}>
                        Add Key
                    </PermissionButton>
                </Tooltip>
                <Tooltip title="Download">
                    <Button variant="outlined" size="small" startIcon={<GetAppIcon />} onClick={() => fetchData(true)}>
                        Download
                    </Button>
                </Tooltip>
            </div>

            <CustomGrid
                columns={columns}
                rows={paginatedData}
                customHeight="calc(100vh - 350px)"
                enableCustomFiltering={true}
                showFilterButton={true}
                showColumnSelector={true}
                remoteFiltering={true}
                applyRemoteFiltering={handleRemoteFiltering}
                applyRemoteSorting={handleRemoteSorting}
                defaultFilters={activeFilters}
                defaultSortModel={activeSortModel}
                onResetAllFilters={handleResetAllFilters}
                showPagination={true}
                currentPage={currentPage}
                pageCount={pageCount}
                totalResults={gridData.length}
                onPageChange={(page) => setCurrentPage(page)}
                onPageCountChange={(count) => { setPageCount(count); setCurrentPage(1); }}
                getRowClassName={getRowClassName}
                defaultHiddenColumns={defaultHiddenColumns}
                onSaveDefaults={handleSaveDefaults}
            />

            <EditDialog
                open={editDialogOpen}
                onClose={handleEditClose}
                onSave={handleEditSave}
                row={editingRow}
                fields={editFields}
                title={isNewRecord ? "Add Data Dictionary Key" : "Edit Data Dictionary Key"}
                columns={4}
            />

            <DeleteDialog
                openDeleteDialog={openDeleteDialog}
                title="Delete Key Confirmation"
                handleDeleteDialogClose={handleDeleteDialogClose}
                handleDeleteDialogSubmit={handleDeleteDialogSubmit}
            >
                Are you sure you want to delete the following key:
                <br />
                <div className="deleteDialogKey">
                    {deleteRecord?.key}
                </div>
            </DeleteDialog>
        </div>
    );
}
export default DataDictionary;